import Message, { BridgeMessageTypes } from './bridge-message.types';
import { WarmChatContext } from './prechat.types';

class JavascriptBridge {
  private static instance: JavascriptBridge;

  /**
   * The JavascriptBridge's constructor should always be private to prevent direct
   * construction calls with the `new` operator.
   */
  private constructor() {}

  /**
   * The static method that controls the access to the singleton instance.
   *
   * This implementation let you subclass the JavascriptBridge class while keeping
   * just one instance of each subclass around.
   */
  public static getInstance(): JavascriptBridge {
    if (!JavascriptBridge.instance) {
      JavascriptBridge.instance = new JavascriptBridge();
      JavascriptBridge.instance.doConnect();
    }

    return JavascriptBridge.instance;
  }

  public doConnect() {
    'use client';
    console.log(`**** Called doConnect: ****`);

    try {
      if (window && window?.parent?.OsvcMobileAppBridge_v1) {
        window?.parent?.OsvcMobileAppBridge_v1?.connect(this);
      } else {
        console.log("No message was sent. Couldn't find connect api in window");
      }
    } catch (error) {}
  }

  /**
   *
   * @param message Client session attributes (sessionId, domain, etc) which are all sourceable from the serveraction establishSession (check initialize_from_scratch)
   */
  private sendMessage(message: Message) {
    'use client';

    console.log(`**** Called SendMessage: ****`);
    console.table(message);

    if (!window) {
      return;
    }

    try {
      // iOS webview
      if (/chewy-ios/i.test(window.navigator.userAgent)) {
        try {
          window.webkit.messageHandlers.OsvcMobileAppBridge_v1.postMessage(message);
        } catch (e) {
          console.log('error', `${e}`);
        }
      }
      // Android webview
      else if (/chewy-android/i.test(window.navigator.userAgent)) {
        try {
          window.OsvcMobileAppBridge_v1.postMessage(JSON.stringify(message));
        } catch (e) {
          console.log('error', `${e}`);
        }
      }

      // everything else
      else if (window?.parent?.OsvcMobileAppBridge_v1) {
        try {
          window?.parent?.OsvcMobileAppBridge_v1?.postMessage(message);
        } catch (e) {
          console.log('error', `${e}`);
        }
      } else {
        console.log("No message was sent. Couldn't find postMessage api in window");
      }
    } catch (error) {
      console.log('error', `${error}`);
    }
  }

  /**
   *
   * @param message Client session attributes (sessionId, domain, etc) which are all sourceable from the serveraction establishSession (check initialize_from_scratch)
   */
  public postMessageToApp(message: any) {
    console.log(`**** Recieved postMessage in MWeb: ****`);
    console.table(message);

    // Here you will have to parse the message and do the appropriate actions based on the message type

    switch (message.type) {
      case BridgeMessageTypes.ChangeOrientation:
        console.log('Received changeOrientation message');
        console.table(message);
        break;
      default:
        console.log('Received unknown message type: ');
        console.table(message);
    }
  }

  /**
   *
   * @param message Message to be sent for changeOrientation
   */
  public changeOrientation(message: string) {
    'use client';
    console.log(`**** Called changeOrientation: ****`);
    console.table(message);
  }

  /**
   *
   * @param message Message to be sent for debug
   */
  public debugMessage(message: string) {
    'use client';

    JavascriptBridge.instance.sendMessage({
      type: BridgeMessageTypes.DebugMessage,
      message: message
    });
  }

  /**
   *
   * @param message Message to be sent for error
   * @param code Code to be sent for error
   */
  public errorMessage(code: number, message: string) {
    'use client';

    JavascriptBridge.instance.sendMessage({
      type: BridgeMessageTypes.ErrorMessage,
      message: message,
      code: code
    });
  }

  /**
   *
   * @param url to navigate to
   */
  public navigateToURL(url: string) {
    'use client';

    JavascriptBridge.instance.sendMessage({
      type: BridgeMessageTypes.NavigateToURL,
      url: url
    });
  }

  /**
   *
   * @param url to navigate to
   */
  public navigateToURLMweb(url: string) {
    'use client';

    JavascriptBridge.instance.sendMessage({
      type: BridgeMessageTypes.NavigateToURLMweb,
      url: url
    });
  }

  /**
   * Call to start the chat
   *  * @param chatSessionId pass along
   */
  public startChat(chatSessionId: string, chatEngagementId: string, warmContext: WarmChatContext) {
    'use client';
    JavascriptBridge.instance.sendMessage({
      type: BridgeMessageTypes.StartChat,
      chatSessionId: chatSessionId,
      chatEngagementId: chatEngagementId,
      chatSessionAttributes: JSON.stringify(warmContext)
    });
  }

  /**
   * Call to start ai Chat
   * @param sessionId
   */
  public startAiChat(chatSessionId: string, chatEngagementId: string | null) {
    'use client';
    console.log('Sending start chat event: ');
    const context = {
      service: 'Cai'
    };
    JavascriptBridge.instance.sendMessage({
      type: BridgeMessageTypes.StartChat,
      chatSessionId: chatSessionId,
      chatEngagementId: chatEngagementId ?? chatSessionId, // placeholder - remove and throw UI error
      chatSessionAttributes: JSON.stringify(context)
    });
  }
  /**
   * Call to close the chat
   */
  public closeChat(closeView = true) {
    'use client';

    JavascriptBridge.instance.sendMessage({
      type: BridgeMessageTypes.CloseChat,
      closeView: closeView
    });
  }

  /**
   *
   * @param subtype value to specify what the analytics are related to
   * @param data data to be stored about that metric
   */
  public analyticsEvent(subtype: string, data: any) {
    'use client';

    JavascriptBridge.instance.sendMessage({
      type: BridgeMessageTypes.AnalyticsEvent,
      subtype: subtype,
      data: data
    });
  }

  /**
   * Call to end the chat
   */
  public endChat() {
    'use client';

    JavascriptBridge.instance.sendMessage({
      type: BridgeMessageTypes.EndChat
    });
  }

  public minimizeChat(reason: string) {
    'use client';

    JavascriptBridge.instance.sendMessage({
      type: BridgeMessageTypes.MinimizeChat,
      reason: reason
    });
  }
}

export default JavascriptBridge;
